import { useMemo, useRef } from 'react'
import { useQuery } from 'react-query'

import { useIsFetchingNewPeriod } from '@features/expertReports'

import { defaultStaleTime } from '../../../config/queryClient'
import { QueryKeys } from '../../../enums/queryKeys'
import type { APIError } from '../../../utils'
import { fetchExpertProfitAndLoss, type FetchExpertProfitAndLossParams } from '../query-api'
import type { ExpertProfitAndLossReport } from '../types/expertProfitAndLossReport'

interface UseFetchExpertProfitAndLossResult {
  displayedSearch: string
  isError: boolean
  isFetchingNewPeriod: boolean
  isLoading: boolean
  report: ExpertProfitAndLossReport | undefined
  /** Same report as `report`, but never affected by `search` - use for values that must stay global (e.g. the result header). */
  unfilteredReport: ExpertProfitAndLossReport | undefined
}

export const useFetchExpertProfitAndLoss = ({
  compareCount,
  compareWith,
  organizationId,
  period,
  search,
}: FetchExpertProfitAndLossParams): UseFetchExpertProfitAndLossResult => {
  const hasActiveSearch = !!search?.trim()

  const queryProps = useMemo<FetchExpertProfitAndLossParams>(
    () => ({ compareCount, compareWith, organizationId, period, search }),
    [compareCount, compareWith, organizationId, period, search],
  )

  const {
    data: reportData,
    isError,
    isIdle,
    isLoading,
    isPreviousData,
  } = useQuery<ExpertProfitAndLossReport, APIError>(
    [QueryKeys.ExpertProfitAndLoss, queryProps],
    () => fetchExpertProfitAndLoss(queryProps),
    { enabled: !!organizationId && !!period, keepPreviousData: true, staleTime: defaultStaleTime },
  )

  const unfilteredQueryProps = useMemo<FetchExpertProfitAndLossParams>(
    () => ({ compareCount, compareWith, organizationId, period, search: undefined }),
    [compareCount, compareWith, organizationId, period],
  )

  // Same query key/params as the base query when there is no active search, so this is a cache hit rather than a duplicate request.
  const { data: unfilteredReportData } = useQuery<ExpertProfitAndLossReport, APIError>(
    [QueryKeys.ExpertProfitAndLoss, unfilteredQueryProps],
    () => fetchExpertProfitAndLoss(unfilteredQueryProps),
    { enabled: !!organizationId && !!period && hasActiveSearch, keepPreviousData: true, staleTime: defaultStaleTime },
  )

  const lastSettledSearchRef = useRef(search ?? '')
  if (!isPreviousData) {
    lastSettledSearchRef.current = search ?? ''
  }

  const isFetchingNewPeriod = useIsFetchingNewPeriod(period, isPreviousData)
  const hidePreviousPeriod = isError && isFetchingNewPeriod
  const report = hidePreviousPeriod ? undefined : reportData
  const unfilteredReport = hidePreviousPeriod || !hasActiveSearch ? report : unfilteredReportData

  return {
    displayedSearch: lastSettledSearchRef.current,
    isError,
    isFetchingNewPeriod: isFetchingNewPeriod && !isError,
    isLoading: isIdle || isLoading,
    report,
    unfilteredReport,
  }
}
